import React, { useState, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormLabel, FormGroup, FormControl, FilledInput, TextField, Button, Icon, IconButton, Tooltip } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { DataGrid } from '@material-ui/data-grid';
import Autocomplete from '@material-ui/lab/Autocomplete';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import DateRange from '../../common/date-range';

import AuthContext from '../../context';
import helpers from '../../utils/helpers';
import { get } from '../../utils/api-services';

const useStyles = makeStyles(() => ({
    root: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    searchBtn: {
        alignSelf: 'flex-end',
        marginBottom: 20,
        backgroundColor: '#0d6efd',
        borderColor: '#0d6efd',
        '&:hover': {
            backgroundColor: '#0d6efd',
            borderColor: '#0d6efd',
            boxShadow: 'none',
        }
    },
    tableContainer: {
        width: '100%'
    }
}));

const defSupplier = { id: 0, name: 'All suppliers' };
const defLocation = { id: 0, name: 'All locations' };

const TicketSaleIndex = ({ permissions }) => {

    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const [state, setState] = useState({
        loading: true,
        suppliers: [],
        locations: [],
        data: [],
        filter: {
            code: '',
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`,
            supplier: defSupplier,
            location: defLocation
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        },
        permissions: {}
    });

    useEffect(() => {
        setState(prev => ({ ...prev, permissions }));
    }, [permissions]);

    useEffect(() => {
        window.document.title = 'Ticket sales';

        makeRequest();
    }, []);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/ticketSale', state.filter);
        if (response.status === 200) {
            setState(prev => ({
                ...prev,
                loading: false,
                data: response?.data ?? [],
                suppliers: response?.suppliers ?? [],
                locations: response?.locations ?? []
            }));
        } else {
            console.log('[TicketSaleIndex] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            filter: {
                ...prev.filter,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleChange = ({ target: { name, value } }) => setState(prev => ({ ...prev, filter: { ...prev.filter, [name]: value } }));

    const handleChangeAutocomplete = (key, value) => setState(prev => ({ ...prev, filter: { ...prev.filter, [key]: value } }));

    const search = async () => {
        showLoader(true);
        const response = await get('/ticketSale/search', state.filter);
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response?.data ?? [] }));

            if (!response?.data[0]) showToast('There are no tickets with these filters', 'warning');
        } else {
            console.log('[TicketSaleIndex] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const renderDate = ({ value }) => dayjs(value).format('MM/DD/YYYY');
    const renderTotal = ({ value }) => `$ ${value}`;
    const valueGetter = ({ value }) => value.name;

    const renderCell = ({ row }) => {
        return (
            <Tooltip title='Show detail' placement='right'>
                <Link to={`/ticket/ticketSale/${row.idticket_sale}`} target='_blank'>
                    <IconButton>
                        <Icon>{'visibility'}</Icon>
                    </IconButton>
                </Link>
            </Tooltip>
        );
    }

    const columns = [
        { field: 'code', headerName: 'Code', flex: 0.5 },
        { field: 'total', headerName: 'Total', flex: 1, valueGetter: renderTotal },
        { field: 'date', headerName: 'Date', flex: 1, valueGetter: renderDate },
        { field: 'supplier', headerName: 'Supplier', flex: 1, valueGetter },
        { field: 'location', headerName: 'Location', flex: 1, valueGetter },
        { field: 'detail', headerName: 'Detail', flex: 0.5, renderCell }
    ]

    if (state.loading) return <Loader />;

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Code'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='text'
                                placeholder='Code'
                                name='code'
                                onChange={handleChange}
                                value={state.filter.code}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup onClick={handleModal}>
                        <FormLabel>{'Date range'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                readOnly
                                autoComplete='off'
                                type='text'
                                placeholder='Date range'
                                name='range'
                                value={state.filter.dateRange}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Supplier'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.suppliers}
                            value={state.filter.supplier}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('supplier', value)}
                            renderInput={params => <TextField {...params} placeholder='Supplier' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} lg={3}>
                    <FormGroup>
                        <FormLabel>{'Location'}</FormLabel>
                        <Autocomplete
                            disabled={Boolean(state.filter.code.trim())}
                            options={state.locations}
                            value={state.filter.location}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('location', value)}
                            renderInput={params => <TextField {...params} placeholder='Location' />}
                        />
                    </FormGroup>
                </Grid>
            </Grid>

            <Button
                variant='contained'
                className={classes.searchBtn}
                onClick={search}
            >
                {'Search'}
            </Button>

            <div className={classes.tableContainer}>
                <DataGrid
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    autoHeight
                    getRowId={row => row.idticket_sale}
                />
            </div>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                onClose={handleModal}
                onChange={handleDateChange}
            />
        </div>
    );
}

export default TicketSaleIndex;